/**
 * App shell — the chrome every in-app screen sits inside.
 *
 * Mounted once and reused. Screens render into the stage area, so switching from
 * the tree to a profile does not rebuild the navigation rail or refetch the tree
 * switcher's counts.
 *
 * The rail mirrors `MENU` in the prototype exactly, including the order and the
 * five group headings, because the grouping is a product decision: "Every day"
 * comes first because the front door is Today, not the tree. Nobody visits a
 * monument on a Tuesday.
 */

import { signOut } from '../api/auth.js';
import { esc } from '../lib/dom.js';
import { ROUTES, router } from '../router.js';
import { mountKeeper } from '../ui/keeper.js';
import { mountWall, openWall } from '../ui/wall.js';
import { session } from '../state/session.js';

/**
 * Navigation, grouped as in the prototype.
 *
 * `[path, label, icon, phase, subs]` — phase is shown as a muted marker on screens that
 * are not built yet, so it is always clear whether something is missing or broken.
 *
 * `subs` are the prototype's rail sub-menus: the four items that open onto more than one thing
 * list those things underneath, revealed when the parent is clicked. They are shortcuts, not
 * navigation of their own — "Create a poll" goes to the polls tab *and* opens the form, which is
 * two steps a relative would otherwise take by hand every time.
 *
 * Each sub is `[label, href]`. The href carries the tab and, where there is one, the form to
 * open — so a sub is a real link that survives being opened in a new window, rather than a
 * button that only works from a click handler.
 *
 * The glyphs are typographic marks drawn in the rail's ink. Two of them — the writing hand
 * and the tent — are characters the platform prefers to draw as full-colour emoji, so they
 * arrived as the only two coloured things on a monochrome rail. Each is followed by U+FE0E,
 * the text presentation selector: the same character, asked for as a letter rather than a
 * picture. It is invisible in an editor, so those two entries look one character short.
 */
export const MENU = [
  ['Every day', [
    ['/app', 'Today', '☀', 1],
    ['/app/on-this-day', 'On this day', '◷', 1],
  ]],
  ['The tree', [
    ['/app/tree', 'Family Tree', '⌂', 0],
    ['/app/add', 'Add Family Member', '+', 0, [
      ['Parent', '/app/add?rel=parent'],
      ['Spouse', '/app/add?rel=spouse'],
      ['Sibling', '/app/add?rel=sibling'],
      ['Aunt / Uncle', '/app/add?rel=aunt-uncle'],
      ['Children', '/app/add?rel=child'],
      ['Grandparent', '/app/add?rel=grandparent'],
      ['Cousin', '/app/add?rel=cousin'],
    ]],
    ['/app/invite', 'Invite Family Members', '✉', 0],
    ['/app/album', 'Family Album', '▣', 1],
    ['/app/photos-by-email', 'Photos by email', '✉', 1],
    ['/app/video', 'Video Messages', '▶', 3, [
      ['All recordings', '/app/video?tab=all'],
      ['Record for myself', '/app/video?tab=myself'],
      ['Record about a loved one', '/app/video?tab=loved'],
      ['Request a recording', '/app/video?tab=request'],
      ['Legacy letters', '/app/video?tab=sealed'],
    ]],
    ['/app/memorials', 'Memorials', '✦', 3],
  ]],
  ['Together', [
    ['/app/blog', 'Blogs & Polls', '✍︎', 1, [
      ['Create a blog', '/app/blog?tab=threads&new=post'],
      ['Create a poll', '/app/blog?tab=polls&new=poll'],
      ['Search threads', '/app/blog?tab=search'],
    ]],
    ['/app/messages', 'Messages', '✆', 2],
    ['/app/recipes', 'Family Recipes', '♨', 2],
    ['/app/map', 'Family Map', '◉', 4],
    ['/app/calls', 'Group Video Calls', '◎', 4],
  ]],
  ['Care', [
    ['/app/support', 'Being Supportive', '♥', 2, [
      ['Meal train', '/app/support?tab=meal'],
      ['Prayer chain', '/app/support?tab=prayer'],
      ['Fund a cause', '/app/support?tab=fund'],
      ['Family reunion fund', '/app/support?tab=reunionfund'],
      ['Elder care', '/app/support?tab=elder'],
    ]],
    ['/app/money', 'Money Transfer', '$', 2],
    ['/app/safe', 'Tracker & Mark Safe', '⚑', 4],
  ]],
  ['Run the day', [
    ['/app/calendar', 'Family Calendar', '▤', 1],
    ['/app/private-calendar', 'Private Calendar', '▥', 4],
    ['/app/magic-import', 'Magic Import', '✦', 1],
    ['/app/meals', 'Meal Planning', '◍', 4],
    ['/app/chores', 'Chores & Rewards', '★', 4],
    ['/app/events', 'Events & Invites', '✧', 1],
    ['/app/reunion', 'Reunion Planner', '⛺︎', 4],
    ['/app/cabinet', 'Filing Cabinet', '▤', 3],
    ['/app/calendars', 'Connected Calendars', '⟲', 1],
  ]],
];

/**
 * Which routes are backed by a screen. Everything else shows its phase.
 *
 * Asked of the router rather than kept as a list here. A route with a `load` has a screen
 * behind it; one without falls through to the placeholder. That is the same fact the rail is
 * trying to draw, and holding a second copy of it meant the rail went stale the moment a
 * screen was built — Memorials was finished, working, and still greyed out with a P3 against
 * it, because nobody remembered there were two places to say so.
 */
const BUILT = new Set(ROUTES.filter((route) => route.load).map((route) => route.path));

let shellRoot = null;
let stage = null;
let feedrail = null;

/**
 * Ensure the shell exists inside `container` and return the stage element.
 *
 * Idempotent: called on every in-app render, builds once.
 */
export function ensureShell(container) {
  if (shellRoot && container.contains(shellRoot)) {
    paintRail();
    paintTreeTabs();
    return stage;
  }

  container.innerHTML = shellHTML();
  shellRoot = container.querySelector('.shell');
  stage = container.querySelector('#stagearea');
  feedrail = container.querySelector('#feedrail');

  // Once, when the shell is built. Screens that change the count refresh it themselves.
  refreshNeeds();

  // Bound here rather than in shellHTML because the shell is built once and reused
  // across every in-app navigation — a listener added per render would stack up.
  //
  // `signOut()` clears the session synchronously and retires the refresh token in
  // the background, so navigating immediately is safe: by the time the marketing
  // page renders there is no session left for it to read.
  shellRoot.querySelector('#shellSignOut')?.addEventListener('click', () => {
    signOut();
    router.navigate('/');
  });

  wireTheMenu(shellRoot);
  shellRoot.querySelector('#wallButton')?.addEventListener('click', openWall);
  wireTheTreeTabs(shellRoot);

  paintRail();
  paintTreeTabs();
  loadFeedRail();

  // Keeper sits in the corner of the whole app, as the prototype has it — half the product
  // points at it, and those buttons were dead ends while the panel did not exist. Mounted on
  // the shell rather than per screen, so a conversation survives navigating away.
  mountKeeper(shellRoot);

  // The kitchen wall. Mounted on the shell rather than given a route, because it is a mode
  // the product enters and leaves, not a page somebody navigates back from — the prototype
  // puts its button in the header for the same reason.
  mountWall(shellRoot);

  return stage;
}

/**
 * Switch family.
 *
 * `session.switchTree` was written, `main.js` has listened for `tree-changed` since the
 * beginning, and `paintTreeTabs` has been drawing the tabs with `data-tree` on them — and
 * nothing ever bound a click to them. Four families across the top of every screen, and no way
 * to move between them.
 *
 * Found while checking something else entirely: the recordings list looked identical on two
 * families, which had two possible causes — a switcher that did nothing, or one family reading
 * another's records. It was the first. (The second is now pinned by a test, because it is the
 * worse of the two by a long way.)
 *
 * Delegated on the shell rather than on the tab holder: the holder's contents are rewritten by
 * `paintTreeTabs` on every navigation, and a listener on a replaced element stops existing.
 */
function wireTheTreeTabs(root) {
  root.addEventListener('click', (event) => {
    const tab = event.target.closest('[data-tree]');
    if (!tab) return;

    const treeId = tab.dataset.tree;
    if (treeId === session.activeTreeId) return;

    // `switchTree` throws when the id is not one of theirs, which can only happen if the tabs
    // are stale. Repainting is the honest answer: it redraws from what the session actually
    // holds, so the family they cannot reach stops being offered.
    try {
      session.switchTree(treeId);
    } catch {
      paintTreeTabs();
      return;
    }

    // The `on` class moves immediately, before the route has re-resolved. The alternative is a
    // press that appears to do nothing while the next screen loads.
    paintTreeTabs();
  });
}

/** Highlight the current route in the rail. Called after every navigation. */
/**
 * How many things are waiting on you, as the number beside Today.
 *
 * The prototype's own badge — `["today","Today","☀", null, 5]` — and its five are the five rows
 * in its "Needs you" card. So this counts the same thing the card lists rather than inventing a
 * second number beside it: a badge saying 5 above a card showing 3 is the kind of disagreement
 * nobody reports and everybody stops trusting.
 *
 * Held here rather than fetched by `paintRail`, which runs on every navigation and on every
 * repaint inside a screen. A request on each of those would be dozens a minute for a number
 * that changes when somebody else does something.
 */
let needsCount = 0;

/** Re-read the count from the server and repaint the badge. Safe to call at any time. */
export async function refreshNeeds() {
  const { today } = await import('../api/people.js');
  const body = await today().catch(() => null);
  // A failure leaves the last known number rather than clearing it: a badge that empties
  // because one request failed reads as "nothing is waiting on you", which is a lie.
  if (body) needsCount = (body.needs ?? []).length;
  paintRail();
}

export function paintRail() {
  if (!shellRoot) return;

  const badge = shellRoot.querySelector('[data-needs]');
  if (badge) {
    badge.textContent = needsCount > 99 ? '99+' : String(needsCount);
    badge.hidden = needsCount === 0;
  }

  const here = window.location.pathname;
  let openSection = null;

  shellRoot.querySelectorAll('.navitem').forEach((node) => {
    const path = node.getAttribute('href');
    // The tree screen and a profile are the same section as far as the rail is
    // concerned — a relative who clicked into Dada's page has not left the tree.
    const active =
      path === here || (path === '/app/tree' && here.startsWith('/app/people/'));
    node.classList.toggle('on', active);
    if (active) openSection = path;
  });

  // The sub-menu of the section you are in, and only that one.
  //
  // Driven by the current route rather than by a click, so it is right after a reload, after
  // following a link somebody sent, and after the back button — three cases a toggle stored in
  // the DOM gets wrong.
  shellRoot.querySelectorAll('.sub').forEach((node) => {
    node.classList.toggle('open', node.dataset.sub === openSection);
  });
}

/**
 * Paint the tree switcher.
 *
 * Every tree the user belongs to, because belonging to four is the normal case:
 * their own, their spouse's, and each parent's.
 */
export function refreshFeedRail() {
  loadFeedRail({ force: true });
}

/**
 * Re-read the tree counts and repaint the switcher.
 *
 * `session.trees` is written once, when the account loads, and the number on each pill comes
 * from it. So adding or removing a relative left the count untouched until the next full
 * reload — a tab open across an afternoon of adding cousins kept insisting the family had the
 * size it had at breakfast. Caught on a tree that read 38 while the server said 30.
 *
 * Called by the screens that change who is on the tree rather than on every navigation: this
 * is a request, and paying for it when nothing has changed is how a switcher costs a round
 * trip per click.
 *
 * Deliberately quiet on failure. A stale number is a small wrong thing; an error dialogue
 * over the top of a relative who has just been added successfully is a larger one.
 */
export async function refreshTreeTabs() {
  try {
    const { loadMe } = await import('../api/auth.js');
    await loadMe();
    paintTreeTabs();
  } catch {
    // Left as it was.
  }
}

export function paintTreeTabs() {
  const holder = shellRoot?.querySelector('#trees');
  if (!holder) return;

  const trees = session.trees ?? [];

  if (trees.length === 0) {
    holder.innerHTML = '<span class="small" style="opacity:.7">No family trees yet</span>';
    return;
  }

  holder.innerHTML = trees
    .map((tree) => {
      const on = tree.tree_id === session.activeTreeId ? ' on' : '';
      // People on the tree, not accounts that have signed in. `member_count` rendered
      // the Khan tab as "Khan Family 1" on a tree of twenty-eight relatives.
      const count = tree.person_count ?? tree.member_count ?? 0;
      return `<button class="tree-tab${on}" data-tree="${esc(tree.tree_id)}">
          ${esc(tree.tree_name)}<span class="n">${esc(count)}</span>
        </button>`;
    })
    .join('');
}

/**
 * "Upcoming", under the navigation.
 *
 * The prototype's own block, and the numbers beside each row are the point: "09 invited ·
 * 168 yes". An organiser cannot book a hall until they know how many have answered, and
 * that is what `rsvp=NONE` exists as a stored row to make countable.
 *
 * Birthdays are left out on purpose. They are already on the calendar and the home screen,
 * and a list of what needs organising should not be four birthdays deep before it reaches
 * the reunion deposit.
 */
export function paintUpcoming(items) {
  const holder = shellRoot?.querySelector('#upcoming');
  if (!holder) return;

  if (!items.length) {
    holder.innerHTML = '';
    return;
  }

  holder.innerHTML = `
    <div class="grp">Upcoming</div>
    ${items
      .map(
        (item) => `
      <a class="upnext" href="/app/calendar?year=${item.on_date.slice(0, 4)}&month=${Number(
        item.on_date.slice(5, 7),
      )}">
        <span class="dot" style="background:${esc(item.colour)}"></span>
        <span class="bd">
          ${esc(item.title)}
          <span>${esc(item.when)}${
            item.invited
              ? ` · ${item.answered} of ${item.invited} answered`
              : ''
          }</span>
        </span>
      </a>`,
      )
      .join('')}`;
}

/**
 * The face in the corner, or the initials standing in for one.
 *
 * Initials rather than a silhouette: this product draws them everywhere else a person has no
 * photograph — on the tree, on a sender chip, beside a name in the directory — and a grey
 * outline of a head would be the one place it said "no picture" instead of saying who.
 */
function faceHTML() {
  const name = session.user?.display_name ?? '';
  const url = session.user?.avatar_url;

  if (url) {
    return `<img src="${esc(url)}" alt="${esc(name)}">`;
  }

  const initials = name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0].toUpperCase())
    .join('');

  return `<span>${esc(initials || '?')}</span>`;
}

function shellHTML() {
  return `
  <div class="shell">
    <div class="app-top">
      <a class="logo" href="/" style="font-size:1.1rem;text-decoration:none">
        <svg class="knot" viewBox="0 0 24 24" fill="none" stroke="#A8791C" stroke-width="2.2" width="18" height="18">
          <path d="M12 22V9M12 9c0-4 3-7 7-7M12 9C12 5 9 2 5 2"/>
        </svg> Linyay
      </a>
      <div class="trees" id="trees"></div>
      <button class="btn ghost sm" type="button" id="wallButton" style="margin-left:auto"
              title="Show the family's day full screen, for a tablet or TV on the wall">
        &#9970; Wall display</button>
      <div class="me-hold" id="meHold">
        <button class="me-btn" type="button" id="meButton"
                aria-haspopup="menu" aria-expanded="false" aria-label="Your account">
          ${faceHTML()}
        </button>

        <!--
          A menu, not a row of buttons. Sign out is one press from a photograph, so it is the
          second item and never the first — and it is a button rather than a link because it
          performs an action: it must not be middle-clicked into a new tab or followed.
        -->
        <div class="me-menu" id="meMenu" role="menu" hidden>
          <a role="menuitem" href="/app/account">Profile</a>
          <button role="menuitem" type="button" id="shellSignOut">Sign out</button>
        </div>
      </div>
    </div>
    <div class="app-body">
      <nav class="rail" id="rail">${railHTML()}<div id="upcoming"></div></nav>
      <main class="stagearea" id="stagearea"></main>
      <aside class="feedrail" id="feedrail"></aside>
    </div>
  </div>`;
}

function railHTML() {
  return MENU.map(
    ([group, items]) => `
      <div class="grp">${esc(group)}</div>
      ${items
        .map(([path, label, icon, phase, subs]) => {
          const built = BUILT.has(path);
          const marker = built ? '' : `<span class="cnt phase">P${esc(phase)}</span>`;
          return `<a class="navitem${built ? '' : ' unbuilt'}" href="${esc(path)}"
                     ${subs ? `data-has-sub="${esc(path)}"` : ''}>
              <span class="ic">${icon}</span>
              <span class="lbl">${esc(label)}</span>${marker}${
                path === '/app' ? '<span class="cnt" data-needs hidden></span>' : ''
              }
            </a>
            ${
              subs
                ? `<div class="sub" data-sub="${esc(path)}">
                     ${subs
                       .map(
                         ([subLabel, href]) =>
                           `<a href="${esc(href)}">${esc(subLabel)}</a>`,
                       )
                       .join('')}
                   </div>`
                : ''
            }`;
        })
        .join('')}`,
  ).join('');
}


/**
 * The rail's own label for a path.
 *
 * Exported so the unbuilt-screen placeholder can name the screen the way the rail
 * names it. It used to print the route key — "onthis", "album", "support" — which is
 * an internal identifier, and reads to anyone else as though something is broken.
 */
export function labelFor(path) {
  for (const [, items] of MENU) {
    for (const [itemPath, label] of items) {
      if (itemPath === path) return label;
    }
  }
  return null;
}


/**
 * The right-hand activity strip.
 *
 * The prototype's third column: what the family has been doing, newest first, beside
 * whatever screen you are on. Painted from the shell rather than from a screen because it
 * belongs to the shell — it is there on every screen, and a screen that repainted it would
 * make it flicker on every navigation.
 *
 * @param {object|null} data  {week: string[], items: post[]} or null while loading
 */
/**
 * Fetch the strip once per shell mount.
 *
 * Guarded, because `ensureShell` is called by every screen and the strip does not change
 * between navigations. A failure leaves the column empty rather than taking the screen with
 * it — nothing here is load-bearing.
 */
let feedRailLoaded = false;

export function loadFeedRail({ force = false } = {}) {
  if (feedRailLoaded && !force) return;
  feedRailLoaded = true;

  paintFeedRail(null);

  import('../api/feed.js')
    .then(({ rail }) => rail())
    .then((data) => {
      paintFeedRail(data);
      paintUpcoming(data.upcoming ?? []);
    })
    .catch(() => {
      // Let a later mount try again.
      feedRailLoaded = false;
      paintFeedRail({ week: [], items: [] });
      paintUpcoming([]);
    });
}

export function paintFeedRail(data) {
  if (!feedrail) return;

  if (data === null) {
    feedrail.innerHTML = `<div class="mono-note">Family feed</div>`;
    return;
  }

  const week = data.week ?? [];
  const items = data.items ?? [];
  const treeName = session.activeTree?.tree_name ?? 'Family';

  feedrail.innerHTML = `
    <div class="mono-note">${esc(treeName)} feed</div>

    ${
      week.length
        ? `<div class="card" style="margin:12px 0;background:var(--plum-lt);border-color:#E4CBD8;padding:14px">
             <span class="pill">This week</span>
             <p style="font-size:.85rem;margin:.6em 0 0;color:#4E1E36">
               ${esc(sentence(week))}
             </p>
           </div>`
        : ''
    }

    ${items.map(feedItem).join('')}

    ${
      items.length
        ? `<div style="margin-top:12px"><a class="btn ghost sm" href="/app/blog">Open the feed</a></div>`
        : `<p class="small">Nothing on the feed yet. A question, a story, a poll — it stays searchable forever.</p>`
    }`;
}

/**
 * The week's facts as one sentence.
 *
 * The prototype writes it as prose: "This week: Farid's grandfather died, Iram's meal train
 * is half filled, Zara turned 1, and two households still owe reunion deposits." Joining
 * with an Oxford-style "and" is what makes counted facts read like somebody wrote them.
 */
function sentence(lines) {
  if (lines.length === 1) return `${lines[0]}.`;
  const head = lines.slice(0, -1).join(', ');
  return `${head}, and ${lines[lines.length - 1]}.`;
}

function feedItem(post) {
  const who = post.author_detail?.display_name ?? 'Someone';
  const replies = post.reply_count
    ? `${post.reply_count} ${post.reply_count === 1 ? 'reply' : 'replies'}`
    : 'no replies yet';

  return `
  <a class="feeditem" href="/app/blog?tab=threads" style="display:block;text-decoration:none;color:inherit">
    <div class="who">
      ${esc(who)}
      ${post.is_cooled ? '<span class="pill">cooled</span>' : ''}
      ${post.kind === 'medical_history' ? '<span class="pill need">medical</span>' : ''}
    </div>
    <div class="txt">${esc(post.title)}</div>
    <div class="meta">${esc(post.kind_label)} · ${esc(replies)}</div>
  </a>`;
}

/**
 * Open and close the account menu.
 *
 * Bound once, with the shell, because the shell is built once and kept across every navigation
 * — a listener added per render would stack up and fire the same handler a dozen times by
 * lunchtime.
 *
 * Three ways out, and all three are needed: pressing the button again, pressing anywhere else,
 * and Escape. A menu that only closes by its own button is one somebody leaves open and then
 * clicks *through* on their next tap.
 */
function wireTheMenu(root) {
  const button = root.querySelector('#meButton');
  const menu = root.querySelector('#meMenu');
  if (!button || !menu) return;

  const shut = () => {
    menu.hidden = true;
    button.setAttribute('aria-expanded', 'false');
  };

  button.addEventListener('click', (event) => {
    event.stopPropagation();
    const opening = menu.hidden;
    menu.hidden = !opening;
    button.setAttribute('aria-expanded', opening ? 'true' : 'false');
    if (opening) menu.querySelector('a, button')?.focus();
  });

  // Choosing something closes it — including Profile, which navigates within the app and would
  // otherwise leave the menu hanging over the page it just opened.
  menu.addEventListener('click', shut);
  document.addEventListener('click', shut);
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') shut();
  });
}

/**
 * Redraw the face after somebody changes their photograph.
 *
 * The shell is built once, so a new photograph on the account page would otherwise not show in
 * the corner until a full reload — and the corner is the only place it is visible.
 */
export function repaintFace() {
  const button = document.querySelector('#meButton');
  if (button) button.innerHTML = faceHTML();
}
